import React from 'react';
import { Plus, Trash2, Link as LinkIcon, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { AudioRefRow } from '@/components/AudioTaskFields';

const TypeToggle = ({ type, onChange, index }) => (
  <div className="inline-flex h-10 shrink-0 items-center rounded-md bg-slate-100 p-0.5">
    {[['link', 'Link', LinkIcon, 'text-violet-700'], ['text', 'Text', FileText, 'text-emerald-700']].map(([v, l, Icon, c]) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        data-testid={`edit-ref-toggle-${v}-${index}`}
        className={cn('flex h-9 items-center gap-1 rounded px-2.5 text-xs font-medium transition-colors',
          type === v ? `bg-white shadow-sm ${c}` : 'text-slate-500 hover:text-slate-700')}
      >
        <Icon className="h-3.5 w-3.5" /> {l}
      </button>
    ))}
  </div>
);

export const TaskReferencesEditor = ({ value, onChange, allowText }) => {
  const refs = value || [];
  const update = (i, patch) => onChange(refs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(refs.filter((_, idx) => idx !== i));
  const add = (type) => onChange([...refs, { type, value: '' }]);

  return (
    <div className="flex flex-col gap-3" data-testid="edit-task-references">
      <div className="flex items-center justify-between">
        <Label className="text-slate-700 font-medium">{allowText ? 'References' : 'Reference Links'}</Label>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => add('link')} className="text-[#2EB4AC] hover:text-[#25908A]" data-testid="edit-add-link-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Link
          </Button>
          {allowText && (
            <Button type="button" variant="ghost" size="sm" onClick={() => add('text')} className="text-emerald-700 hover:text-emerald-800" data-testid="edit-add-text-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Text
            </Button>
          )}
        </div>
      </div>
      {refs.length === 0 && <p className="text-xs text-slate-400">No references yet</p>}
      {refs.map((r, i) => (
        <div key={i} className="flex items-start gap-2">
          {r.type === 'audio' ? (
            <AudioRefRow name={r.name} url={r.value} testId={`edit-ref-audio-${i}`} />
          ) : (<>
          {allowText && <TypeToggle type={r.type} index={i} onChange={(type) => update(i, { type })} />}
          {r.type === 'text' ? (
            <Textarea
              value={r.value}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder="Reference note"
              className="min-h-[40px] flex-1"
              data-testid={`edit-ref-input-${i}`}
            />
          ) : (
            <Input
              value={r.value}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder="https://..."
              className="h-10 flex-1"
              data-testid={`edit-ref-input-${i}`}
            />
          )}
          </>)}
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} className="h-10 w-10 shrink-0 text-slate-400 hover:text-red-500" data-testid={`edit-ref-remove-${i}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};
